import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  pickRandomLandmark,
  getStoredLandmark,
  applyLandmarkBackground,
  clearLandmarkBackground,
} from '../utils/spanishBackgrounds';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Bearer-token fallback ─────────────────────────────────────────────────────
// Some browsers (Safari with ITP, mobile in-app webviews, Firefox in strict
// mode, anti-tracking extensions) silently drop or block our HttpOnly auth
// cookies even on first-party requests. To survive those environments we ALSO
// keep the access/refresh tokens in localStorage and attach them as an
// Authorization: Bearer header on every API request. The backend prefers the
// cookie when present; the header is just a safety net.
const TOKEN_KEY = 'habla.access_token';
const REFRESH_KEY = 'habla.refresh_token';
const getStoredAccess = () => { try { return localStorage.getItem(TOKEN_KEY); } catch { return null; } };
const getStoredRefresh = () => { try { return localStorage.getItem(REFRESH_KEY); } catch { return null; } };
const setStoredTokens = (access, refresh) => {
  try {
    if (access) localStorage.setItem(TOKEN_KEY, access);
    if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
  } catch { /* private mode, storage full, etc. */ }
};
const clearStoredTokens = () => {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
  } catch { /* ignore */ }
};

// Inject Authorization header on every API request (only when a token exists).
// Runs once at module load; safe to re-run because axios deduplicates the same
// interceptor only by reference (we install just one).
axios.interceptors.request.use((config) => {
  try {
    const url = String(config.url || '');
    if (!url.includes(API) && !url.startsWith('/api')) return config;
    const t = getStoredAccess();
    if (t) {
      config.headers = config.headers || {};
      if (!config.headers.Authorization) config.headers.Authorization = `Bearer ${t}`;
    }
  } catch { /* never block the request */ }
  return config;
});

// Global 401 interceptor: auto-refresh once, and on failure force logout so the
// user is redirected to /login instead of getting stuck inside the app with a
// stale token. Also handles the "multiple devices" scenario — a background 401
// on device B triggers the refresh flow instead of breaking the session.
let _refreshing = null;
const doRefresh = () => axios.post(
  `${API}/auth/refresh`,
  // Send the stored refresh token in the body too, in case cookies are blocked.
  { refresh_token: getStoredRefresh() },
  { withCredentials: true },
).then(res => {
  if (res.data?.access_token) setStoredTokens(res.data.access_token, res.data.refresh_token);
  return res;
});

axios.interceptors.response.use(
  (r) => r,
  async (error) => {
    const { response, config } = error;
    if (!response || response.status !== 401 || !config) throw error;
    if (config._retry) throw error;
    const url = String(config.url || '');
    if (url.includes('/auth/refresh') || url.includes('/auth/login') || url.includes('/auth/me')) {
      throw error;
    }
    config._retry = true;
    try {
      _refreshing = _refreshing || doRefresh();
      await _refreshing;
      _refreshing = null;
      // Reissue the original request with the (possibly new) stored token.
      const t = getStoredAccess();
      if (t) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${t}`;
      }
      return axios(config);
    } catch (e) {
      _refreshing = null;
      try { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }); } catch {}
      clearStoredTokens();
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
        window.location.href = '/login';
      }
      throw error;
    }
  }
);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Global "Lehrer-Demo" / "Ver como alumno" mode. Persisted in sessionStorage
  // so it survives a reload but resets on a new browser session. Only
  // teachers/admins can activate it; everywhere we check it we still cross-
  // check user.role so a privilege bug in this flag can NEVER expose a
  // student to teacher-only data.
  const [demoMode, _setDemoMode] = useState(() => {
    try { return sessionStorage.getItem('habla.demo_mode') === '1'; } catch { return false; }
  });
  const setDemoMode = useCallback((v) => {
    _setDemoMode(v);
    try {
      if (v) sessionStorage.setItem('habla.demo_mode', '1');
      else sessionStorage.removeItem('habla.demo_mode');
    } catch { /* private mode */ }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      // Access token expired → silently refresh with the long-lived (365-day)
      // refresh token (cookie OR localStorage) and retry once.
      if (error.response?.status === 401) {
        try {
          await doRefresh();
          const retry = await axios.get(`${API}/auth/me`, { withCredentials: true });
          setUser(retry.data);
        } catch (e) {
          setUser(null);
        }
      } else {
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
    // Restore the previously-chosen landmark on page reload so the same photo
    // stays for the whole session (avoids a flicker between reloads).
    const stored = getStoredLandmark();
    if (stored) applyLandmarkBackground(stored);
  }, [checkAuth]);

  // Persistent session: the refresh token lives 365 days and is re-issued on
  // every refresh, so users stay logged in until they press "Abmelden".
  // We refresh every 30 min while the app is open and whenever the tab
  // becomes visible again.
  useEffect(() => {
    if (!user) return;
    const refresh = () => doRefresh().catch(() => {});
    const tickId = setInterval(refresh, 30 * 60 * 1000);
    const onVis = async () => {
      if (document.visibilityState !== 'visible') return;
      refresh();
      // Re-check identity in case another tab logged in as a different user.
      try {
        const me = await axios.get(`${API}/auth/me`, { withCredentials: true });
        if (me.data?.user_id && me.data.user_id !== user.user_id) {
          setUser(me.data);
        }
      } catch { /* will be caught by interceptor */ }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(tickId);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [user?.user_id]);

  const login = async (email, password) => {
    // Retry on network errors / 5xx (cold starts, flaky connections). Do NOT retry on 401/403/429.
    let lastErr = null;
    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        const response = await axios.post(`${API}/auth/login`, { email, password }, {
          withCredentials: true,
          timeout: 15000
        });
        // Persist Bearer-token fallback so future requests work even if the
        // browser blocked our cookies (Safari ITP, in-app webviews, etc.).
        if (response.data?.access_token) {
          setStoredTokens(response.data.access_token, response.data.refresh_token);
        }
        // Always setUser so downstream guards can see `must_change_password`
        // and redirect to /change-password. Previously we intentionally skipped
        // setUser in this case, which left the app in an unauthenticated
        // limbo — the redirect gate in ProtectedRoute relies on user being set.
        setUser(response.data);
        // Rotate the Spain-landmark background on every successful login so
        // the student sees a different corner of the country each session.
        applyLandmarkBackground(pickRandomLandmark());
        return response.data;
      } catch (err) {
        lastErr = err;
        const status = err.response?.status;
        if (status && status >= 400 && status < 500 && status !== 408) {
          throw err;
        }
        if (attempt < 3) await new Promise(r => setTimeout(r, 600 * attempt));
      }
    }
    throw lastErr;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData, {
      withCredentials: true
    });
    if (response.data?.access_token) {
      setStoredTokens(response.data.access_token, response.data.refresh_token);
    }
    setUser(response.data);
    // Meta Pixel — fire CompleteRegistration / Lead. Only emits if the user
    // gave marketing consent; the helper guards loadedness internally.
    try {
      const { trackEvent } = await import('../utils/metaPixel');
      trackEvent('CompleteRegistration', { content_name: 'habla_signup' });
      trackEvent('Lead', { content_name: 'habla_signup' });
    } catch { /* pixel disabled */ }
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
    clearStoredTokens();
    clearLandmarkBackground();
    setDemoMode(false);
    setUser(null);
  };

  const refreshAuth = async () => {
    await checkAuth();
  };

  // Used by set-password / invite pages that return a fresh session.
  const loginWithTokens = (data) => {
    if (data?.access_token) setStoredTokens(data.access_token, data.refresh_token);
    const u = data?.user || data;
    if (u?.user_id) setUser(u);
  };

  const changePassword = async ({ current_password, new_password }) => {
    const res = await axios.post(
      `${API}/auth/change-password`,
      { current_password, new_password },
      { withCredentials: true }
    );
    // Optimistically clear the flag locally so the forced-change gate steps
    // aside on the next render. checkAuth() will re-sync the rest of the doc.
    setUser(prev => (prev ? { ...prev, must_change_password: false } : prev));
    return res.data;
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      register,
      logout,
      refreshAuth,
      loginWithTokens,
      changePassword,
      setUser,
      demoMode,
      setDemoMode
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
